import { createAction } from 'redux-actions';
import {
  SET_SYSTEM_MESSAGES,
  SET_ADMIN_MESSAGES,
  SET_SCHEDULED_MESSAGES,
  SET_LAST_ADMIN_MESSAGE_ID,
  CREATE_MESSAGE_SUCCESS,
  REQUEST_FAIL,
  TOGGLE_PUSH_MESSAGES_MODAL,
  TOGGLE_FILTER_SETTINGS_MODAL,
  SET_COUPLING_TABLE_DATA,
  SET_SELECTED_FILTERS,
  REFRESH_FILTERS,
  SET_MESSAGES_PAGE,
  SET_DONOR_MESSAGES,
  CLEAR_DONOR_MESSAGES,
} from './actionTypes';
import {
  get,
  createPushMessage,
} from '../../services/fetch';
import { getIdToken } from "../auth/actions";
import { addPendingRequest, abortPendingRequest, deletePendingRequestIfCurrent } from '../requests/actions';
import { getAbortController } from '../../services/helpers';
import { toast } from 'react-toastify';
import i18n from '../../i18n';
import { messagesSortConfig } from '../../constants/pushMessages';

export const setSystemMessages = createAction(SET_SYSTEM_MESSAGES);
export const setAdminMessages = createAction(SET_ADMIN_MESSAGES);
export const setScheduledMessages = createAction(SET_SCHEDULED_MESSAGES);
export const setLastAdminMessageId = createAction(SET_LAST_ADMIN_MESSAGE_ID);
export const createMessageSuccess = createAction(CREATE_MESSAGE_SUCCESS);
export const requestFail = createAction(REQUEST_FAIL);
export const togglePushMessagesModal = createAction(TOGGLE_PUSH_MESSAGES_MODAL);
export const toggleFilterSettingsModal = createAction(TOGGLE_FILTER_SETTINGS_MODAL);
export const setCouplingTableData = createAction(SET_COUPLING_TABLE_DATA);
export const setSelectedFilters = createAction(SET_SELECTED_FILTERS);
export const refreshFilters = createAction(REFRESH_FILTERS);
// payload: { type: 'admin'|'system'|'scheduled', bloodbankId, content: [...],
//            paging: { page, page_size, total, total_pages, sort?, order? } (server),
//            requested: { page, pageSize, sort, order } (what the thunk asked for) }
export const setMessagesPage = createAction(SET_MESSAGES_PAGE);
// payload: { uid, bloodbankId, content: [...], truncated: boolean, error: null|string }
export const setDonorMessages = createAction(SET_DONOR_MESSAGES);
export const clearDonorMessages = createAction(CLEAR_DONOR_MESSAGES);

// Message history is paged AND sorted SERVER-SIDE:
//   GET /v1/bloodbanks/{id}/messages?type=<type>&page=<1-based>&page_size=<25|50|100>&sort=<key>&order=<asc|desc>
//   -> { content: [...], paging: { page, page_size, total, total_pages, sort?, order? } }
// The portal shows one page at a time: the fetched page simply replaces the
// per-type list, and the classic pager (Antall rader / Forrige / Neste) is
// driven by `paging.total`. `sort`/`order` are always sent; the sortable keys
// and the per-tab default are in constants/pushMessages MESSAGES_SORT (an
// unknown sort is coerced to the tab default here — the API answers 400).
// The API clamps the page offset at MESSAGES_MAX_OFFSET (page <= 401 at size
// 25): the pager is capped client-side to the same range.
export const MESSAGES_PAGE_SIZES = [25, 50, 100];
export const MESSAGES_DEFAULT_PAGE_SIZE = 25;
export const MESSAGES_MAX_OFFSET = 10000;
export const MESSAGES_LIST_KEY = {
  admin: 'adminMessages',
  system: 'systemMessages',
  scheduled: 'scheduledMessages',
};

const normalizePageSize = (pageSize) =>
  (MESSAGES_PAGE_SIZES.includes(Number(pageSize)) ? Number(pageSize) : MESSAGES_DEFAULT_PAGE_SIZE);
const normalizePage = (page) => (Number.isInteger(Number(page)) && Number(page) >= 1 ? Number(page) : 1);

// { key, order } the tab can actually ask the server for
export const normalizeMessagesSort = (type, sort, order) => {
  const config = messagesSortConfig(type);
  const key = config.server.includes(sort) ? sort : config.defaultSort.key;
  const direction = order === 'asc' || order === 'desc' ? order : config.defaultSort.order;
  return { key, order: direction };
};

export const messagesUrl = (type, bloodbankId, { page, pageSize, sort, order }) => {
  const sorting = normalizeMessagesSort(type, sort, order);
  const params = new URLSearchParams({
    type,
    page: String(page),
    page_size: String(pageSize),
    sort: sorting.key,
    order: sorting.order,
  });
  return `/v1/bloodbanks/${bloodbankId}/messages?${params.toString()}`;
};

// Guarded GET for the listener-driven message fetches: a newer same-key call
// aborts its in-flight predecessor (requests/actions addPendingRequest), so a
// burst of Firestore snapshot events can never stack N concurrent requests
// for the same list — only the newest one lands. Always resolves (an abort
// or a failure is swallowed after dispatching requestFail) so callers can
// clear a spinner in `.finally`.
const guardedGet = (functionKey, url, onSuccess) => (dispatch) => {
  const { signal, abortController } = getAbortController();
  dispatch(addPendingRequest({ id: functionKey, controller: abortController }));

  return get(url, { signal })
  .then((res) => dispatch(onSuccess(res)))
  .catch((err) => {
    // superseded by a newer same-key call — not an error surface
    if (err.name === 'AbortError') return;
    return dispatch(requestFail(err));
  })
  .finally(() => dispatch(deletePendingRequestIfCurrent({ id: functionKey, controller: abortController })));
};

// One page of one history type; the fetched page replaces that type's list.
// Same pending-request key for every page of a type, so a page change, a
// tab/bloodbank switch and a listener-driven refetch all abort whatever
// same-type request is still in flight.
export const getMessages = (type, bloodbankId, { page = 1, pageSize = MESSAGES_DEFAULT_PAGE_SIZE, sort, order } = {}) => {
  const sorting = normalizeMessagesSort(type, sort, order);
  const requested = {
    page: normalizePage(page),
    pageSize: normalizePageSize(pageSize),
    sort: sorting.key,
    order: sorting.order,
  };
  return guardedGet(`getMessages.${type}`, messagesUrl(type, bloodbankId, requested),
    (res) => setMessagesPage({ type, bloodbankId, content: res && res.content, paging: res && res.paging, requested }));
};

// Every message ONE donor received, across all three history types and
// regardless of the paged tabs:
//   GET /v1/bloodbanks/{id}/donors/{uid}/messages?limit=200
//   -> { content: [{ id, source: 'push'|'scheduled', type, title, body,
//                    timestamp, scheduledFor, sent, hasDeviceToken,
//                    fromAdmin }], truncated }   (timestamp DESC)
// Errors the API defines for this route: 403 {error: donor_not_in_bloodbank}
// (the donor belongs to another bank) and 404 (no such donor) — both are
// surfaced as a toast AND stored as `error` on the result, so the screen
// never shows an empty "no messages" for a donor whose messages it could
// not load. Any other failure — including a 403 that is NOT that code (a
// role/claims rejection) — goes through requestFail as usual (and is stored
// as `request_failed` for the same reason).
// The result is tagged with BOTH the donor and the bank it was asked for:
// the screen only shows it when both match what is on screen.
// Always resolves: with DONOR_MESSAGES_ABORTED when the request was aborted
// (nothing is stored then), so the caller can tell "superseded/cancelled"
// from "landed".
export const DONOR_MESSAGES_LIMIT = 200;
export const DONOR_MESSAGES_PENDING_KEY = 'getDonorMessages';
export const DONOR_MESSAGES_ABORTED = Object.freeze({ aborted: true });
export const DONOR_MESSAGES_ERROR = {
  NOT_IN_BLOODBANK: 'donor_not_in_bloodbank',
  NOT_FOUND: 'donor_not_found',
  REQUEST_FAILED: 'request_failed',
};

export const donorMessagesUrl = (bloodbankId, uid) =>
  `/v1/bloodbanks/${bloodbankId}/donors/${uid}/messages?limit=${DONOR_MESSAGES_LIMIT}`;

const donorMessagesError = (err) => {
  if (err.status === 403 && err.body && err.body.error === DONOR_MESSAGES_ERROR.NOT_IN_BLOODBANK) {
    return DONOR_MESSAGES_ERROR.NOT_IN_BLOODBANK;
  }
  if (err.status === 404) return DONOR_MESSAGES_ERROR.NOT_FOUND;
  return DONOR_MESSAGES_ERROR.REQUEST_FAILED;
};

export const getDonorMessages = (bloodbankId, uid) => (dispatch) => {
  const { signal, abortController } = getAbortController();
  dispatch(addPendingRequest({ id: DONOR_MESSAGES_PENDING_KEY, controller: abortController }));

  return get(donorMessagesUrl(bloodbankId, uid), { signal })
  .then((res) => dispatch(setDonorMessages({
    uid,
    bloodbankId,
    content: res && Array.isArray(res.content) ? res.content : [],
    truncated: Boolean(res && res.truncated),
    error: null,
  })))
  .catch((err) => {
    // superseded by a newer call (another donor / bank) or cancelled (search
    // cleared, bank switched, screen left) — not an error surface
    if (err.name === 'AbortError') return DONOR_MESSAGES_ABORTED;
    const error = donorMessagesError(err);
    if (error === DONOR_MESSAGES_ERROR.REQUEST_FAILED) {
      dispatch(requestFail(err));
    } else {
      toast.error(i18n.t(`msgs.donor.error.${error}`), { position: toast.POSITION.BOTTOM_RIGHT });
    }
    return dispatch(setDonorMessages({ uid, bloodbankId, content: [], truncated: false, error }));
  })
  .finally(() => dispatch(deletePendingRequestIfCurrent({ id: DONOR_MESSAGES_PENDING_KEY, controller: abortController })));
};

// Cancels the donor fetch in flight, if any (search cleared / another PNR /
// bank switch / screen left): its late response must never be stored.
export const abortDonorMessages = () => abortPendingRequest(DONOR_MESSAGES_PENDING_KEY);

export const getAdminMessages = (bloodbankId, paging) => getMessages('admin', bloodbankId, paging);
export const getSystemMessages = (bloodbankId, paging) => getMessages('system', bloodbankId, paging);
export const getScheduledMessages = (bloodbankId, paging) => getMessages('scheduled', bloodbankId, paging);

// POST /v1/admin-messages is asynchronous: 202 + the queued
// sendPushAdminInit record; a scheduled function does the fan-out later
// (status queued -> processing -> done|failed, visible in the admin list via
// the sendPushAdminInit listener). Success here means QUEUED, not sent.
export const createAdminMessage = (data) => (dispatch) => {
  dispatch(getIdToken())
  .then((token) => createPushMessage(`/v1/admin-messages`, {...data, token}))
  .then(() => {
    toast.success(i18n.t('msgs.queued'), { position: toast.POSITION.BOTTOM_RIGHT });
    return dispatch(createMessageSuccess());
    }
  )
  .catch((err) => dispatch(requestFail(err)));
}

export const getLastAdminMessage = () =>
  guardedGet('getLastAdminMessage', '/v1/admin-messages/last', setLastAdminMessageId);

export const getCouplingTableData = () => (dispatch) => {
  // Legacy /coupling was a TS stub that always returned {content:true};
  // dropped from the v1 surface by design (docs/api-path-review.md).
  return dispatch(setCouplingTableData({ content: true }));
}
